import { apiFetch } from '@/api/request'
import type { ApiResponse } from '@/types/verification'
import type { SellerSession } from '@/constants/sellerSession'

export interface SellerAvailabilityResult {
  available: boolean
}

export interface SellerSignUpPayload {
  sellerId: string
  password: string
  passwordConfirm: string
  storeName: string
  bizName: string
  bizNumber: string
  ownerName: string
  managerName: string
  email: string
  phone: string
  zipcode: string
  address: string
  addressDetail: string
  agreements: {
    terms: boolean
    privacy: boolean
    settlement: boolean
  }
}

export interface SellerSignUpResult {
  sellerId: string
  sellerCode: string
  status: string
}

export interface SellerLoginPayload {
  sellerId: string
  password: string
  /** 자동 로그인. 서버가 쿠키 수명을 12시간 대신 30일로 잡는다. */
  rememberMe?: boolean
}

export interface SellerProfilePayload {
  storeName: string
  bizName: string
  ownerName: string
  managerName: string
  email: string
  phone: string
  zipcode: string
  address: string
  addressDetail: string
  settleBank: string
  settleAccount: string
  settleHolder: string
  shippingFee: number
  freeShipOver: number
  returnFee: number
  cutoff?: string
  returnPolicy?: string
  notify?: {
    order: boolean
    claim: boolean
    stock: boolean
    inquiry: boolean
    settlement: boolean
  }
}

interface SellerResult {
  seller: SellerSession
}

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const BASE = `${API_ORIGIN}/api/mall/seller`

const fetchOptions: RequestInit = {
  credentials: 'include',
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다. 백엔드와 프론트 dev 서버를 확인해 주세요.')
  }

  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) {
    throw new Error('요청 처리에 실패했습니다.')
  }
  return data
}

export async function checkSellerId(sellerId: string) {
  const query = new URLSearchParams({ sellerId })
  const response = await apiFetch(`${BASE}/seller-id/check?${query.toString()}`, fetchOptions)
  const result = await parseJson<SellerAvailabilityResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '아이디 확인에 실패했습니다.')
  }
  return result.data
}

export async function checkSellerBizNumber(bizNumber: string) {
  const query = new URLSearchParams({ bizNumber })
  const response = await apiFetch(`${BASE}/biz-number/check?${query.toString()}`, fetchOptions)
  const result = await parseJson<SellerAvailabilityResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '사업자등록번호 확인에 실패했습니다.')
  }
  return result.data
}

export async function signUpSeller(payload: SellerSignUpPayload) {
  const response = await apiFetch(`${BASE}/signup`, {
    ...fetchOptions,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const result = await parseJson<SellerSignUpResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '판매업체 회원가입에 실패했습니다.')
  }
  return result.data
}

export async function loginSeller(payload: SellerLoginPayload) {
  const response = await apiFetch(`${BASE}/login`, {
    ...fetchOptions,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const result = await parseJson<SellerResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '로그인에 실패했습니다.')
  }
  return result.data.seller
}

export async function logoutSeller() {
  const response = await apiFetch(`${BASE}/logout`, {
    ...fetchOptions,
    method: 'POST',
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '로그아웃에 실패했습니다.')
  }
}

export async function fetchSellerSession() {
  const response = await apiFetch(`${BASE}/session`, fetchOptions)
  const result = await parseJson<SellerResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '판매업체 세션을 확인할 수 없습니다.')
  }
  return result.data.seller
}

export async function updateSellerProfile(payload: SellerProfilePayload) {
  const response = await apiFetch(`${BASE}/profile`, {
    ...fetchOptions,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const result = await parseJson<SellerResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '판매업체 정보 저장에 실패했습니다.')
  }
  return result.data.seller
}

export interface SellerLegalDoc {
  code: string
  version: string
  title: string
  body: string
  /** TERMS=이용약관, CONTRACT=공급·판매 계약서 */
  kind?: 'TERMS' | 'CONTRACT'
  effective?: string
}

export async function fetchSellerOnboarding() {
  const response = await apiFetch(`${BASE}/onboarding`, fetchOptions)
  const result = await parseJson<{
    seller: SellerSession
    docs: SellerLegalDoc[]
    intro: string
  }>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '온보딩 정보를 불러오지 못했습니다.')
  }
  return result.data
}

export interface SellerOnboardingPayload {
  storeName: string
  bizName: string
  bizNumber: string
  ownerName: string
  managerName: string
  email: string
  phone: string
  zipcode: string
  address: string
  addressDetail: string
  settleBank: string
  settleAccount: string
  settleHolder: string
  newPassword?: string
  signature: string
  /** 계약서는 pdfBase64(순수 base64)를 함께 보내 서버에 체결본으로 보관한다 */
  accepts: Array<{ code: string; version: string; pdfBase64?: string }>
}

export async function submitSellerOnboarding(payload: SellerOnboardingPayload) {
  const response = await apiFetch(`${BASE}/onboarding`, {
    ...fetchOptions,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const result = await parseJson<SellerResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '온보딩 제출에 실패했습니다.')
  }
  return result.data.seller
}

export interface SellerLegalAcceptance {
  idx: number
  code: string
  version: string
  title?: string
  body?: string
  kind?: 'TERMS' | 'CONTRACT'
  signedAt: string
  signature: string
  pdfPath?: string
  hasPdf?: boolean
}

/** 체결 시 저장된 계약서 PDF를 내려받는다 */
export async function downloadMyAcceptancePdf(idx: number, filename = 'contract.pdf') {
  const response = await apiFetch(`${BASE}/acceptances/${encodeURIComponent(String(idx))}/pdf`, {
    ...fetchOptions,
    method: 'GET',
  })
  if (!response.ok) {
    let message = '저장된 계약서 PDF를 내려받지 못했습니다.'
    try {
      const json = (await response.json()) as { message?: string }
      if (json?.message) message = json.message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export async function fetchMyLegalAcceptances() {
  const response = await apiFetch(`${BASE}/acceptances`, fetchOptions)
  const result = await parseJson<{
    accepts: SellerLegalAcceptance[]
    activeDocs?: SellerLegalDoc[]
  }>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '계약·약관 내역을 불러오지 못했습니다.')
  }
  return {
    accepts: result.data.accepts || [],
    activeDocs: result.data.activeDocs || [],
  }
}

export interface SellerNotifyItem {
  idx: number
  type: string
  title: string
  body: string
  orderCode: string
  read: boolean
  createdAt: string
}

export async function fetchSellerNotifies() {
  const response = await apiFetch(`${BASE}/notifies`, fetchOptions)
  const result = await parseJson<{ items: SellerNotifyItem[]; unread: number }>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '알림을 불러오지 못했습니다.')
  }
  return result.data
}

export async function markSellerNotifiesRead(idx?: number) {
  const response = await apiFetch(`${BASE}/notifies/read`, {
    ...fetchOptions,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(idx ? { idx } : {}),
  })
  const result = await parseJson<{ unread: number }>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '알림을 처리하지 못했습니다.')
  }
  return result.data.unread
}
